import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ApartmentStats, PayoutStat} from "@/models/responses";
import {ColDef, GridApi} from "ag-grid-community";
import {euroFormatterTwoDecimals, paramsEuroFormatter, percentageFormatterMultipliedByHundred, redOrGreenText} from "@/utils/formatters";
import Grid, {GridDropdownFilter} from "@/lib/organisms/Grid";
import {CustomCellRendererProps} from "ag-grid-react";
import {PeriodName} from "@/models";
import {useAppData} from "@/hooks/useAppData";
import {useUserSettingsDispatch} from "@/store/slices/userSettingsDispatch";
import {useAllApartmentEntities} from "@/store/slices/apartments";
import {getPeriodStartAndEndDates} from "@/utils/periods";
import {useGetApartmentStatsQuery, useGetPayoutsQuery} from "@/store/booking-management-api";
import {useApartmentFilters} from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

interface PayoutsRow {
    apartment: string;
    daysBooked: number;
    daysAvailable: number;
    netAdr: number;
    grossIncome: number;
    cleaningFees: number;
    commissions: number;
    netIncome: number;
    taxes: number;
    businessFee: number;
    payout: number;
    payoutRatio: number;
    managementFee: number;
    expenses: number;
    periodDeposit: number;
    cumulativeDeposit: number;
}

const PayoutsPage: React.FC = () => {
    const {yearOptions, periodOptions, selectedPeriod, selectedYear, groupOptions, selectedGroup, apartmentIDMap, apartmentDetailsMap} = useAppData();
    const apartmentEntities = useAllApartmentEntities();

    const {setSelectedYear, setSelectedPeriod, setSelectedGroup} = useUserSettingsDispatch();

    const {predicates} = useApartmentFilters();

    const grossIncomeColumn: ColDef<PayoutsRow> = {
        headerName: "Gross Income",
        field: "grossIncome",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.grossIncome !== undefined) {
                return Math.round(value.data.grossIncome);
            }
        },
    };
    const cleaningFeesColumn: ColDef<PayoutsRow> = {
        headerName: "Cleaning Fees",
        field: "cleaningFees",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 120,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.cleaningFees !== undefined) {
                return Math.round(value.data.cleaningFees);
            }
        },
    };
    const commissionsColumn: ColDef<PayoutsRow> = {
        headerName: "Commissions",
        field: "commissions",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 150,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.commissions !== undefined) {
                return Math.round(value.data.commissions);
            }
        },
    };
    const netIncomeColumn: ColDef<PayoutsRow> = {
        headerName: "Net Income",
        field: "netIncome",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.netIncome !== undefined) {
                return Math.round(value.data.netIncome);
            }
        },
    };
    const taxesColumn: ColDef<PayoutsRow> = {
        headerName: "Taxes",
        field: "taxes",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.taxes !== undefined) {
                return Math.round(value.data.taxes);
            }
        },
    };
    const businessFeeColumn: ColDef<PayoutsRow> = {
        headerName: "Business Fee",
        field: "businessFee",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.businessFee !== undefined) {
                return Math.round(value.data.businessFee);
            }
        },
    };
    const payoutColumn: ColDef<PayoutsRow> = {
        headerName: "Payout",
        field: "payout",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.payout !== undefined) {
                return Math.round(value.data.payout);
            }
        },
    };
    const managementFeeColumn: ColDef<PayoutsRow> = {
        headerName: "Management Fee",
        field: "managementFee",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.managementFee !== undefined) {
                return Math.round(value.data.managementFee);
            }
        },
    };
    const expensesColumn: ColDef<PayoutsRow> = {
        headerName: "Expenses",
        field: "expenses",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.expenses !== undefined) {
                return Math.round(value.data.expenses);
            }
        },
    };
    const cumulativeDepositColumn: ColDef<PayoutsRow> = {
        headerName: "Cumulative Deposit",
        field: "cumulativeDeposit",
        valueFormatter: paramsEuroFormatter,
        cellClassRules: redOrGreenText,
        cellClass: "currencyFormat",
        cellDataType: "number",
        cellRenderer: (params: CustomCellRendererProps) => {
            const color = params.value > 0 ? "green" : "red";
            return (
                <div
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: color === "green" ? "#dcfce7" : "#fee2e2",
                    }}
                    className=""
                >
                    <span className={`w-full p-2 text-right text-${color}-600`}>{paramsEuroFormatter(params)}</span>
                </div>
            );
        },
        width: 120,
        filter: "agNumberColumnFilter",
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.cumulativeDeposit !== undefined) {
                return Math.round(value.data.cumulativeDeposit);
            }
        },
    };
    const depositColumn: ColDef<PayoutsRow> = {
        headerName: "Period Deposit",
        field: "periodDeposit",
        valueFormatter: paramsEuroFormatter,
        cellClassRules: redOrGreenText,
        cellClass: "currencyFormat",
        cellDataType: "number",
        cellRenderer: (params: CustomCellRendererProps) => {
            const color = params.value > 0 ? "green" : "red";
            return (
                <div
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: color === "green" ? "#dcfce7" : "#fee2e2",
                    }}
                    className=""
                >
                    <span className={`w-full p-2 text-right text-${color}-600`}>{paramsEuroFormatter(params)}</span>
                </div>
            );
        },
        width: 120,
        filter: "agNumberColumnFilter",
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.periodDeposit !== undefined) {
                return Math.round(value.data.periodDeposit);
            }
        },
    };
    const daysBookedColumn: ColDef<PayoutsRow> = {
        headerName: "Days Booked",
        field: "daysBooked",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 120,
    };
    const columnDefinitions: ColDef[] = [
        {
            headerName: "Apartment",
            field: "apartment",
            cellDataType: "text",
            width: 270,
            filter: "agSetColumnFilter",
        },
        daysBookedColumn,
        {
            headerName: "Days Available",
            field: "daysAvailable",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 120,
        },
        {
            headerName: "Net ADR",
            field: "netAdr",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 100,
            filterValueGetter: (value) => {
                return Math.round(value.data.netAdr);
            },
        },
        grossIncomeColumn,
        cleaningFeesColumn,
        commissionsColumn,
        taxesColumn,
        businessFeeColumn,
        netIncomeColumn,
        payoutColumn,
        {
            headerName: "PayoutRatio",
            field: "payoutRatio",
            valueFormatter: percentageFormatterMultipliedByHundred,
            cellDataType: "number",
            cellClass: "booleanType",
            filter: "agNumberColumnFilter",
            width: 120,
            filterValueGetter: (value) => {
                if (value.data !== undefined && value.data.payoutRatio !== undefined) {
                    return Math.round(value.data.payoutRatio * 100);
                }
            },
        },
        managementFeeColumn,
        expensesColumn,
        depositColumn,
        cumulativeDepositColumn,
    ];

    const gridName = "payouts";
    const [apartmentPayouts] = useState<PayoutsRow[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const [startDate, endDate] = useMemo(() => {
        return getPeriodStartAndEndDates(selectedYear, selectedPeriod);
    }, [selectedYear, selectedPeriod]);

    useEffect(() => {
    }, [selectedPeriod, selectedYear]);

    const {data: payouts, isLoading: isPayoutsLoading, error: hasPayoutsErrored} = useGetPayoutsQuery({startDate, endDate});

    const mapPayoutToRow = useCallback(
        (payout: PayoutStat, stats: ApartmentStats | undefined): PayoutsRow => {
            return {
                apartment: apartmentEntities[payout.apartmentId]?.name || payout.apartmentId.toString(),
                daysBooked: stats?.daysBooked || 0,
                daysAvailable: stats?.availableBookingDays || 0,
                netAdr: stats?.netAdr || 0,
                grossIncome: stats?.grossIncome || 0,
                cleaningFees: stats?.cleaningFees || 0,
                commissions: stats?.platformCommissions || 0,
                netIncome: payout.netIncome,
                taxes: stats === undefined ? 0 : stats.vat + stats.sustainabilityFee + stats.temporaryStayDuty,
                payout: payout.payout,
                payoutRatio: payout.payoutRatio,
                managementFee: payout.managementFee,
                expenses: payout.expenses,
                periodDeposit: payout.deposit,
                businessFee: stats?.businessFee || 0,
                cumulativeDeposit: payout.cumulativeDeposit,
            };
        },
        [apartmentEntities],
    );

    const getAndSetPayouts = useCallback(
        (groupId: number, payouts?: PayoutStat[], stats?: ApartmentStats[]) => {
            if (payouts === undefined || stats === undefined || gridApi === null) {
                return;
            }
            let rows = payouts
                .filter((payout) => (groupId === 0 ? true : apartmentIDMap.get(payout.apartmentId)?.groupId === groupId))
                .filter((payout) => {
                    // Apply the shared apartment-filter-bar predicates. An
                    // apartment with no matching entity (e.g. deleted mid-period)
                    // is excluded under any active filter — matches the overview
                    // page semantics. When `predicates` is empty the row passes
                    // unchanged, preserving current behaviour for un-filtered
                    // portfolios. `apartmentDetailsMap` (not `apartmentIDMap`)
                    // because the predicate type is keyed on the `@/models/responses`
                    // Apartment shape, which is what `apartmentDetailsMap` infers
                    // from `useAllApartments`.
                    if (predicates.length === 0) {
                        return true;
                    }
                    const apartment = apartmentDetailsMap.get(payout.apartmentId);
                    if (!apartment) {
                        return false;
                    }
                    return predicates.every((predicate) => predicate(apartment));
                })
                .map((payout) =>
                    mapPayoutToRow(
                        payout,
                        stats.find((stat) => stat.apartmentId === payout.apartmentId),
                    ),
                )
                .sort((a, b) => a.apartment.localeCompare(b.apartment));
            gridApi.setGridOption("rowData", rows);
        },
        [apartmentIDMap, apartmentDetailsMap, gridApi, mapPayoutToRow, predicates],
    );

    const {
        data: stats,
        isLoading: isStatsLoading,
        error: hasStatsErrored,
    } = useGetApartmentStatsQuery({
        startDate,
        endDate,
    });

    useEffect(() => {
        getAndSetPayouts(selectedGroup, payouts, stats);
    }, [payouts, stats, selectedGroup, getAndSetPayouts]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (apartment) => setSelectedGroup(Number(apartment)),
        },
    ];

    if (isPayoutsLoading || isStatsLoading || payouts === undefined || stats === undefined) {
        return (
            <div data-testid="payouts-page">
                <ApartmentFilterBar />
                <div>Loading...</div>
            </div>
        );
    }

    if (hasPayoutsErrored || hasStatsErrored) {
        return (
            <div data-testid="payouts-page">
                <ApartmentFilterBar />
                <div>Error</div>
            </div>
        );
    }

    return (
        <div data-testid="payouts-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <Grid<PayoutsRow>
                columnDefs={columnDefinitions}
                gridName={gridName}
                dropdownFilters={gridDropdownFilters}
                initialRowData={apartmentPayouts}
                getRowId={(params) => params.data.apartment}
                onGridReady={setGridApi}
                columnsToAggregate={[
                {colDef: daysBookedColumn, formatter: (x) => x},
                {
                    colDef: grossIncomeColumn,
                    formatter: euroFormatterTwoDecimals,
                },
                {colDef: cleaningFeesColumn, formatter: euroFormatterTwoDecimals},
                {
                    colDef: commissionsColumn,
                    formatter: euroFormatterTwoDecimals,
                },
                {colDef: taxesColumn, formatter: euroFormatterTwoDecimals},
                {colDef: businessFeeColumn, formatter: euroFormatterTwoDecimals},
                {
                    colDef: netIncomeColumn,
                    formatter: euroFormatterTwoDecimals,
                },
                {colDef: expensesColumn, formatter: euroFormatterTwoDecimals},
                {
                    colDef: payoutColumn,
                    formatter: euroFormatterTwoDecimals,
                },
                {colDef: depositColumn, formatter: euroFormatterTwoDecimals},
                {
                    colDef: cumulativeDepositColumn,
                    formatter: euroFormatterTwoDecimals,
                },
                {colDef: managementFeeColumn, formatter: euroFormatterTwoDecimals},
            ]}
            />
        </div>
    );
};

export default PayoutsPage;
